export const CUSTOMISATION_CATEGORIES = {
  NONE: "none",
  GLOBAL: "global",
  ACTIVITIES: "activities",
  ECOSCORE: "ecoscore",
  PACKAGING_INFO: "packagingInfo",
  FOOTER: "footer",
  LOGO: "logo",
  SEARCH_ENGINE: "searchEngine",
  PRODUCT_ATTRIBUTES: "productAttributes",
  ORGANIZATION_TITLE: "organizationTitle",
  PRODUCT_IMAGE: "productImage",
  GENERIC_PASSPORT_TITLE: "genericPassportTitle",
  DETAILED_PRODUCT_COMPOSITION: "detailedProductComposition",
  ECO_MODULATION: "ecoModulation",
} as const

export const CUSTOMISATION_CATEGORIES_WITHOUT_NONE = {
  GLOBAL: CUSTOMISATION_CATEGORIES.GLOBAL,
  ACTIVITIES: CUSTOMISATION_CATEGORIES.ACTIVITIES,
  ECOSCORE: CUSTOMISATION_CATEGORIES.ECOSCORE,
  PACKAGING_INFO: CUSTOMISATION_CATEGORIES.PACKAGING_INFO,
  FOOTER: CUSTOMISATION_CATEGORIES.FOOTER,
  LOGO: CUSTOMISATION_CATEGORIES.LOGO,
  SEARCH_ENGINE: CUSTOMISATION_CATEGORIES.SEARCH_ENGINE,
  PRODUCT_ATTRIBUTES: CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES,
} as const

export type CustomisationCategory =
  (typeof CUSTOMISATION_CATEGORIES)[keyof typeof CUSTOMISATION_CATEGORIES]

export type CustomisationConfigurationCategory =
  (typeof CUSTOMISATION_CONFIGURATION_CATEGORIES)[keyof typeof CUSTOMISATION_CONFIGURATION_CATEGORIES]

export type CustomisationCategoryWithoutNone = Exclude<
  CustomisationCategory,
  typeof CUSTOMISATION_CATEGORIES.NONE
>

export const CUSTOMISATION_CONFIGURATION_CATEGORIES = {
  ACTIVITIES: CUSTOMISATION_CATEGORIES.ACTIVITIES,
  FOOTER: CUSTOMISATION_CATEGORIES.FOOTER,
  ECO_MODULATION: CUSTOMISATION_CATEGORIES.ECO_MODULATION,
} as const

export const STRING_TYPE_VALUE = "string" as const
export const CLASS_TYPE_VALUE = "class" as const
export const CSS_VARIABLE_TYPE_VALUE = "cssVariable" as const

export enum SETTINGS_SCOPE {
  LANGUAGES = "languages",
  DESIGN_CUSTOMISATION = "design_customisation",
  COMPONENT_CUSTOMISATION = "component_customisation",
  CONTENT_FILTERING = "content_filtering",
  PRODUCT_PAGE_COMPONENTS = "product_page_components",
}

export const CommunicationStudioClasses = {
  ECOSCORE_B2B: "ecoscoreB2b",
  ORGANIZATION_TITLE: "organizationTitle",
  PRODUCT_IMAGE: "productImage",
  PRODUCT_TITLE: "productTitle",
  PRODUCT_NAME: "productName",
  PRODUCT_REFERENCE: "productReference",
  LOGO: "logo",
  APP_HEADER: "appHeader",
  MAIN_CONTAINER: "mainContainer",
  CONTENT_BLOCKS: "contentBlocks",
  LAST_UPDATE: "lastUpdate",
  ACTIVITY: "activity",
  COUNTRY: "country",
  PACKAGING_INFO_HYPER_LINK: "packagingInfoHyperLink",
  PACKAGING_INFO_HEADER: "packagingInfoHeader",
  SEARCH_ENGINE_HEADER: "searchEngineHeader",
  RECYCLED_PACKAGING_KEY_INFO: "recycledPackagingKeyInfo",
  ECOSCORE_HEADER: "ecoscoreHeader",
  GENERIC_PASSPORT_TITLE: "genericPassportTitle",
  DETAILED_PRODUCT_COMPOSITION_TITLE: "detailedProductCompositionTitle",
  DETAILED_PRODUCT_COMPOSITION_CONTENT: "detailedProductCompositionContent",
  ECO_MODULATION_CONTAINER: "ecoModulationContainer",
  ECO_MODULATION_HEADER: "ecoModulationHeader",
  ECO_MODULATION_BODY: "ecoModulationBody",
  ECO_MODULATION_CRITERION_TITLE: "ecoModulationCriterionTitle",
  ECO_MODULATION_SECONDARY: "ecoModulationSecondary",
  ECO_MODULATION_HYPER_LINK: "ecoModulationHyperLink",
  ECO_MODULATION_BONUS_ICON: "ecoModulationBonusIcon",
  ECO_MODULATION_MALUS_ICON: "ecoModulationMalusIcon",
  PRODUCT_ATTRIBUTES_PRIMARY_INFORMATION: "productAttributesPrimaryInformation",
  PRODUCT_ATTRIBUTES_SECONDARY_INFORMATION:
    "productAttributesSecondaryInformation",
  PRODUCT_ATTRIBUTES_CONTAINER: "productAttributesContainer",
} as const

export type CommunicationStudioClass = keyof typeof CommunicationStudioClasses
export type CommunicationStudioClassValue =
  (typeof CommunicationStudioClasses)[CommunicationStudioClass]

export const CommunicationStudioCssVariables = {
  FONT_FAMILY: "fontFamily",
  HEADER_FONT_FAMILY: "headerFontFamily",
  HEADER_FONT_WEIGHT: "headerFontWeight",
  HEADER_FONT_SIZE: "headerFontSize",
  TEXT_FONT_SIZE: "textFontSize",
  LINE_HEIGHT: "lineHeight",
  PADDING_BLOCK: "paddingBlock",
  CONTENT_VERTICAL_SPACING: "contentVerticalSpacing",
  TITLE_COLOR: "titleColor",
  TEXT_COLOR: "textColor",
  TEXT_FONT_WEIGHT: "textFontWeight",
  PADDING_INLINE: "paddingInline",
  HEADER_BACKGROUND_COLOR: "headerBackgroundColor",
  SEARCH_ENGINE_TEXT_COLOR: "searchEngineTextColor",
  PRODUCT_CARD_BADGE_BACKGROUND_COLOR: "productCardBadgeBackgroundColor",
  PRODUCT_CARD_BADGE_TEXT_COLOR: "productCardBadgeTextColor",
  LETTER_SPACING: "letterSpacing",
  HEADER_TEXT_DECORATION: "headerTextDecoration",
  HEADER_TEXT_TRANSFORM: "headerTextTransform",
  HEADER_LINE_HEIGHT: "headerLineHeight",
  ECOSCORE_ALIGN: "ecoscoreAlign",
  LOGO_ALIGN: "logoAlign",
  PRIMARY_ATTRIBUTES_ALIGN: "primaryAttributesAlign",
  SECONDARY_ATTRIBUTES_ALIGN: "secondaryAttributesAlign",
  PRODUCT_ATTRIBUTES_SPACING: "productAttributesSpacing",
  PRODUCT_ATTRIBUTES_MARGIN_BOTTOM: "productAttributesMarginBottom",
  IMAGE_ALIGN: "imageAlign",
} as const

export const TEXT_CONTENT_TYPE = {
  TITLE: "title",
  TEXT: "text",
  HYPERLINK: "hyperlink",
  STRONG: "strong",
} as const

export const CONTENT_BLOCK_TYPE = {
  PARAGRAPH: "paragraph_block",
  TITLE: "title",
} as const

export const GLOBAL_THEME_VARIABLES = [
  "fontFamily",
  "headerFontWeight",
  "headerFontSize",
  "textFontSize",
  "lineHeight",
  "paddingBlock",
  "contentVerticalSpacing",
] as const
